import { TableStaticActions, useModalMultiple, withModalConditionalRender } from '@billy/design-system'
import React, { ReactElement, useCallback } from 'react'

import { useSegment } from '../../../../../../hooks'
import { PeriodData } from '../../../../utils/getPeriodData'
import { VatDeclarationsExportModal as VatDeclarationsExportModalComponent } from '../../../VatDeclarationsExportModal'
import { VatDeclarationsRegisterPeriodPaymentModal } from '../../../VatDeclarationsRegisterPeriodPaymentModal'
import { VatDeclarationsSettlePeriodModal } from '../../../VatDeclarationsSettlePeriodModal'
import { VatDeclarationsVoidPeriodPaymentConfirmationModal } from '../../../VatDeclarationsVoidPeriodPaymentConfirmationModal'
import { VatDeclarationsVoidPeriodSettlementConfirmationModal } from '../../../VatDeclarationsVoidPeriodSettlementConfirmationModal'
import { VatDeclarationsTableStaticActionType } from '../../enums/vatDeclarationsStaticActionType'
import { VatDeclarationsTableStaticActionItem } from '../../types/vatDeclarationsTableStaticActionItem'

const VatDeclarationSettleModal = withModalConditionalRender(VatDeclarationsSettlePeriodModal)
const VatDeclarationRegisterPaymentModal = withModalConditionalRender(VatDeclarationsRegisterPeriodPaymentModal)
const VatDeclarationVoidPaymentModal = withModalConditionalRender(VatDeclarationsVoidPeriodPaymentConfirmationModal)
const VatDeclarationsExportModal = withModalConditionalRender(VatDeclarationsExportModalComponent)
const VatDeclarationsVoidSettlementModal = withModalConditionalRender(
  VatDeclarationsVoidPeriodSettlementConfirmationModal,
)

const getVatDeclarationSettleModalId = (id: string) => `${id}-vat-declaration-settle-modal`
const getVatDeclarationRegisterPaymentModalId = (id: string) => `${id}-vat-declaration-register-payment-modal`
const getVatDeclarationVoidPaymentModalId = (id: string) => `${id}-vat-declaration-void-payment-modal`
const getVatDeclarationsExportModalId = (id: string) => `${id}-vat-declarations-export-modal`
const getVatDeclarationsVoidSettlementModalId = (id: string) => `${id}-vat-declarations-void-settlement-modal`

interface VatDeclarationsTableStaticActionsProps {
  currentCorrection?: { correctionNo: number }
  items: VatDeclarationsTableStaticActionItem[]
  periodData?: PeriodData
  vatDeclarationId: string
  onShowIntegrationForm?: (vatDeclarationId: string) => void
}

export const VatDeclarationsTableStaticActions = ({
  currentCorrection,
  items,
  periodData,
  vatDeclarationId,
  onShowIntegrationForm,
}: VatDeclarationsTableStaticActionsProps): ReactElement => {
  const { track } = useSegment()

  const vatDeclarationSettleModalId = getVatDeclarationSettleModalId(vatDeclarationId)
  const vatDeclarationRegisterPaymentModalId = getVatDeclarationRegisterPaymentModalId(vatDeclarationId)
  const vatDeclarationVoidPaymentModalId = getVatDeclarationVoidPaymentModalId(vatDeclarationId)
  const vatDeclarationsExportModalId = getVatDeclarationsExportModalId(vatDeclarationId)
  const vatDeclarationsVoidSettlementModalId = getVatDeclarationsVoidSettlementModalId(vatDeclarationId)

  const { open: openModal } = useModalMultiple([
    vatDeclarationSettleModalId,
    vatDeclarationRegisterPaymentModalId,
    vatDeclarationVoidPaymentModalId,
  ])

  const handleStaticActionSelect = useCallback(
    (id: string, value: VatDeclarationsTableStaticActionType) => {
      switch (value) {
        /* Only available for integrations, NL for now */
        case VatDeclarationsTableStaticActionType.Settle: {
          track('xxx - bookkeeping - QuickAction Clicked', {
            list: 'Vat Declarations',
            button: VatDeclarationsTableStaticActionType.Settle,
          })
          onShowIntegrationForm?.(vatDeclarationId)
          break
        }
        case VatDeclarationsTableStaticActionType.RegisterPayment: {
          track('xxx - bookkeeping - QuickAction Clicked', {
            list: 'Vat Declarations',
            button: VatDeclarationsTableStaticActionType.RegisterPayment,
          })
          openModal(vatDeclarationRegisterPaymentModalId)
          break
        }
        case VatDeclarationsTableStaticActionType.VoidPayment: {
          track('xxx - bookkeeping - QuickAction Clicked', {
            list: 'Vat Declarations',
            button: VatDeclarationsTableStaticActionType.VoidPayment,
          })
          openModal(vatDeclarationVoidPaymentModalId)
          break
        }
        case VatDeclarationsTableStaticActionType.SubmitManually: {
          track('xxx - bookkeeping - QuickAction Clicked', {
            list: 'Vat Declarations',
            button: VatDeclarationsTableStaticActionType.SubmitManually,
          })
          openModal(vatDeclarationSettleModalId)
          break
        }
        case VatDeclarationsTableStaticActionType.Export: {
          track('xxx - bookkeeping - QuickAction Clicked', {
            list: 'Vat Declarations',
            button: VatDeclarationsTableStaticActionType.Export,
          })
          openModal(vatDeclarationsExportModalId)
          break
        }
        case VatDeclarationsTableStaticActionType.VoidSettlement: {
          track('xxx - bookkeeping - QuickAction Clicked', {
            list: 'Vat Declarations',
            button: VatDeclarationsTableStaticActionType.VoidSettlement,
          })
          openModal(vatDeclarationsVoidSettlementModalId)
          break
        }
      }
    },
    [
      onShowIntegrationForm,
      openModal,
      track,
      vatDeclarationId,
      vatDeclarationRegisterPaymentModalId,
      vatDeclarationSettleModalId,
      vatDeclarationVoidPaymentModalId,
      vatDeclarationsExportModalId,
      vatDeclarationsVoidSettlementModalId,
    ],
  )

  return (
    <>
      <TableStaticActions items={items} onSelect={handleStaticActionSelect} />

      <VatDeclarationSettleModal id={vatDeclarationSettleModalId} vatDeclarationId={vatDeclarationId} />
      <VatDeclarationRegisterPaymentModal
        id={vatDeclarationRegisterPaymentModalId}
        vatDeclarationId={vatDeclarationId}
      />
      <VatDeclarationVoidPaymentModal id={vatDeclarationVoidPaymentModalId} vatDeclarationId={vatDeclarationId} />
      <VatDeclarationsExportModal
        id={vatDeclarationsExportModalId}
        vatDeclarationId={vatDeclarationId}
        period={periodData?.periodSettled}
      />
      <VatDeclarationsVoidSettlementModal
        currentCorrection={currentCorrection}
        id={vatDeclarationsVoidSettlementModalId}
        periodData={periodData}
        vatDeclarationId={vatDeclarationId}
      />
    </>
  )
}
